import { defineStore } from 'pinia'
import {
  onboardingApi,
  ONBOARDING_STEP_IDS,
  type OnboardingStatus,
  type PersonalInfo,
  type Contract,
} from '@/api/onboarding'

export interface JoinStep {
  id: string
  name: string
  description: string
  completed: boolean
  current: boolean
  route: string
}

export interface JoinState {
  status: OnboardingStatus | null
  personalInfo: PersonalInfo | null
  questionnaire: any | null
  contract: Contract | null
  loading: boolean
  error: string | null
}

export const useJoinStore = defineStore('join', {
  state: (): JoinState => ({
    status: null,
    personalInfo: null,
    questionnaire: null,
    contract: null,
    loading: false,
    error: null,
  }),

  getters: {
    steps: (state): JoinStep[] => {
      const completedSteps = state.status?.completedSteps || []
      const currentStep = state.status?.currentStep || 'personal-info'
      const isComplete = state.status?.status === 'completed'

      // 對齊後端 ClientState 全關卡；auditing 無專屬路由，於 StatusPage 內顯示說明。
      const stepDefinitions: Array<{ id: string; name: string; description: string; route?: string }> = [
        { id: 'personal-info', name: '個人資料', description: '填寫個人基本資料', route: '/join/personal-info' },
        { id: 'questionnaire', name: '問卷填寫', description: '填寫風險評估問卷', route: '/join/questionnaire' },
        { id: 'contract', name: '合約簽署', description: '閱讀並簽署服務合約', route: '/join/contract' },
        { id: 'account-opening', name: '開戶申請', description: '選擇券商並送出開戶申請', route: '/join/account-opening' },
        { id: 'acceptance-report', name: '驗收報告', description: '確認驗收項目並簽署系統驗收報告', route: '/join/acceptance-report' },
        { id: 'phone-confirmation', name: '電話確認', description: '預約並等待業務電話確認', route: '/join/phone-confirmation' },
        { id: 'auditing', name: '系統建置', description: '等候系統環境建置與交易啟用' },
      ]

      return stepDefinitions.map(step => ({
        ...step,
        route: step.route ?? '',
        // run（已完成）時所有關卡標記為完成
        completed: isComplete || completedSteps.includes(step.id),
        current: !isComplete && currentStep === step.id,
      }))
    },

    currentStepIndex: (state): number => {
      const currentStep = state.status?.currentStep || 'personal-info'
      return ONBOARDING_STEP_IDS.indexOf(currentStep as (typeof ONBOARDING_STEP_IDS)[number])
    },

    isComplete: (state): boolean => {
      return state.status?.status === 'completed'
    },

    /** 客戶目前是否處於「審核中 / 系統建置」終態前段（auditing 無下一步操作）。 */
    isAuditing: (state): boolean => {
      return state.status?.status !== 'completed' && state.status?.currentStep === 'auditing'
    },

    isRejected: (state): boolean => {
      return state.status?.status === 'rejected'
    },

    nextStepRoute(): string | null {
      const currentStep = this.steps.find(s => s.current)
      return currentStep?.route || null
    },

    isContractSigned: (state): boolean => {
      return !!state.contract
    },
  },

  actions: {
    async fetchStatus() {
      this.loading = true
      this.error = null
      try {
        this.status = await onboardingApi.getStatus()
      } catch (e: any) {
        this.error = e.message || '無法載入簽約狀態'
        throw e
      } finally {
        this.loading = false
      }
    },

    async fetchPersonalInfo() {
      this.loading = true
      this.error = null
      try {
        this.personalInfo = await onboardingApi.getPersonalInfo()
      } catch (e: any) {
        this.error = e.message || '無法載入個人資料'
        throw e
      } finally {
        this.loading = false
      }
    },

    async updatePersonalInfo(info: Partial<PersonalInfo>) {
      this.loading = true
      this.error = null
      try {
        await onboardingApi.updatePersonalInfo(info)
        this.personalInfo = { ...this.personalInfo, ...info } as PersonalInfo
        // Refresh status after update
        await this.fetchStatus()
      } catch (e: any) {
        this.error = e.message || '更新個人資料失敗'
        throw e
      } finally {
        this.loading = false
      }
    },

    /** 檢查目前問卷是否已通過（read-only，不阻斷流程）。 */
    async checkQuestionnaire(): Promise<{ passed: boolean; message: string }> {
      this.error = null
      const result = await onboardingApi.getQuestionnaire()
      this.questionnaire = result
      return result
    },

    /**
     * 客戶確認「我已完成」外部問卷 → 後端重新判定，通過則推進 client state。
     * 回傳是否通過 + 訊息，讓頁面誠實顯示尚未通過的原因（不 throw 假錯誤）。
     */
    async confirmQuestionnaire(): Promise<{ passed: boolean; message: string }> {
      this.loading = true
      this.error = null
      try {
        const result = await onboardingApi.confirmQuestionnaire()
        this.questionnaire = result
        if (result.passed) {
          await this.fetchStatus()
        }
        return result
      } catch (e: any) {
        this.error = e.message || '確認問卷完成失敗'
        throw e
      } finally {
        this.loading = false
      }
    },

    async fetchContract() {
      this.loading = true
      this.error = null
      try {
        this.contract = await onboardingApi.getContract()
      } catch (e: any) {
        this.error = e.message || '無法載入合約'
        throw e
      } finally {
        this.loading = false
      }
    },

    async ensureContract() {
      // 已有合約則直接沿用
      if (this.contract) return this.contract
      this.loading = true
      this.error = null
      try {
        // 取合約金額：優先用聯絡資訊的預設委託金額
        let amount = 0
        try {
          const contactInfo = await onboardingApi.getContactInfo()
          amount = contactInfo.defaultContractAmount ?? 0
        } catch {
          // 取不到聯絡資訊時以 0 建立（後端允許），不阻斷流程
        }
        this.contract = await onboardingApi.createContract(amount)
        await this.fetchStatus()
        return this.contract
      } catch (e: any) {
        this.error = e.message || '建立合約失敗'
        throw e
      } finally {
        this.loading = false
      }
    },

    async signContract(signatureBase64?: string) {
      if (!this.contract) {
        throw new Error('尚未載入合約，無法簽署')
      }
      this.loading = true
      this.error = null
      try {
        await onboardingApi.signContract(this.contract.id, signatureBase64)
        // Refresh contract and status after signing
        await Promise.all([this.fetchContract(), this.fetchStatus()])
      } catch (e: any) {
        this.error = e.message || '簽署合約失敗'
        throw e
      } finally {
        this.loading = false
      }
    },

    async downloadContractPdf() {
      try {
        const blob = await onboardingApi.downloadContractPdf()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'contract.pdf'
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      } catch (e: any) {
        this.error = e.message || '下載合約失敗'
        throw e
      }
    },

    clearError() {
      this.error = null
    },

    reset() {
      this.status = null
      this.personalInfo = null
      this.questionnaire = null
      this.contract = null
      this.loading = false
      this.error = null
    },
  },
})
